/**
 * PRP-WEB-1 W3.3: the result strip after a plan save, from the `PUT /plan` response.
 * "Saved 1 room." then one line per chat estimate: "Kitchen estimate: 6 lines re-measured;
 * 2 typed quantities kept (DRY 1/2 in Kitchen 120 SF, ...)". Only chat estimates linked to the
 * project follow a plan edit (DD4), so an empty list says so.
 */
import type { PlanSaveEstimate, PlanSaveResult } from '../../../lib/planApi';

const count = (v: number | unknown[] | null | undefined): number => (Array.isArray(v) ? v.length : typeof v === 'number' ? v : 0);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** One typed-quantity line: `{ item, room, code, quantity, unit }` (a string, or older shapes, are shown as they come). */
export function describeLine(x: unknown): string {
  if (typeof x === 'string') return x;
  if (!x || typeof x !== 'object') return String(x);
  const o = x as Record<string, unknown>;
  const code = [o.category ?? o.cat, o.selector ?? o.sel].filter(Boolean).join(' ') || (o.code as string) || '';
  const what = (o.item as string) || (o.description as string) || code || 'line';
  const room = (o.room as string) || (o.room_name as string) || '';
  const qty = o.quantity != null ? `${o.quantity}${o.unit ? ` ${o.unit}` : ''}` : '';
  return [code && code !== what ? `${code} ${what}` : what, room && `in ${room}`, qty].filter(Boolean).join(' ');
}

function estimateLine(e: PlanSaveEstimate): string {
  const name = e.name || 'Chat estimate';
  if (e.error) return `${name}: quantities not updated yet (${e.error}). The next chat call brings them up to date.`;
  const typed = e.typed_quantity_lines ?? [];
  const parts = [`${plural(count(e.lines_requantified), 'line')} re-measured`];
  const resized = count(e.rooms_resized);
  if (resized) parts.unshift(plural(resized, 'room') + ' resized');
  if (typed.length) parts.push(`${plural(typed.length, 'typed quantity', 'typed quantities')} kept (${typed.map(describeLine).join('; ')})`);
  return `${name}: ${parts.join('; ')}`;
}

/**
 * B-7561: the server lists the estimates that FOLLOWED this save, and a save that changes no
 * room (groups, the picture) follows none. So "no chat estimate is linked" is read from the
 * project's link state (`linked`: how many chat estimates follow this plan, from the overview;
 * null when unknown), never from an empty `estimates` list.
 */
export function describeSave(res: PlanSaveResult, linked: number | null = null): { headline: string; lines: string[] } {
  const n = count(res.rooms_saved);
  const headline = `Saved ${plural(n, 'room')}.`;
  const ests = res.estimates ?? [];
  if (ests.length) return { headline, lines: ests.map(estimateLine) };
  const roomsChanged = n > 0 || count(res.deleted_room_ids) > 0;
  if (linked === 0) return { headline, lines: ['No chat estimate is linked to this project, so no quantities changed.'] };
  if (!roomsChanged) return { headline, lines: ['No room changed size, so no estimate quantities changed.'] };
  return { headline, lines: [] };
}
